import { showErrorToast, showSuccessToast, isMinimumEnergyIntakeError } from '@/utils/toast-utils';
import { createClient } from '@/lib/apiService';
import { FormData } from './types';
import { Client, CreateClientPayload } from '@/types/client';
import { getValidAllergies, getValidCuisineTypes, getValidPreferences } from '@/lib/clientPreferenceOptions';
import { mapMedicalConditionsToApiValues } from '@/utils/client-medical-conditions';
import { toFoodIntoleranceApiLabels } from '@/utils/food-intolerances';
import {
  calculateAgeInMonthsFromDateString,
  formatDateStringToYyyyMmDd,
} from './date-utils';

export function extractClientIdFromApiResponse(response: unknown): string | undefined {
  if (!response || typeof response !== 'object') return undefined;

  const readId = (value: unknown): string | undefined => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
    const record = value as Record<string, unknown>;
    const id = record.id ?? record.clientId;
    if (id == null) return undefined;
    const normalized = String(id).trim();
    return normalized || undefined;
  };

  const root = response as Record<string, unknown>;
  const data = root.data && typeof root.data === 'object'
    ? (root.data as Record<string, unknown>)
    : undefined;

  return (
    readId(root.client) ||
    readId(data?.client) ||
    readId(data) ||
    readId(root) ||
    undefined
  );
}

export interface SaveClientResult {
  success: boolean;
  clientId?: string;
}

const normalizeActivityToken = (value: string): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[()]/g, '')
    .replace(/[\s-]+/g, '_');

function parseCsvValues(value?: string | null): string[] {
  return String(value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function mapAllergyNameToValue(value: string): string {
  const normalized = value.trim().toLowerCase();
  const matched = getValidAllergies().find(
    (allergy) => allergy.value.toLowerCase() === normalized || allergy.name.toLowerCase() === normalized
  );
  return matched?.value ?? value.trim();
}

function mapDietaryPreferenceNameToValue(value: string): string {
  const normalized = value.trim().toLowerCase();
  const matched = getValidPreferences().find(
    (preference) =>
      preference.value.toLowerCase() === normalized || preference.name.toLowerCase() === normalized
  );
  return matched?.value ?? value.trim();
}

function mapCuisineTypeNameToValue(value: string): string {
  const normalized = value.trim().toLowerCase();
  const matched = getValidCuisineTypes().find(
    (cuisine) =>
      cuisine.value.toLowerCase() === normalized || cuisine.name.toLowerCase() === normalized
  );
  return matched?.value ?? value.trim();
}

const PRIMARY_GOAL_ADJUSTMENTS = new Set([-1000, -500, -250, 0, 250, 500, 750]);

const mapActivityLevel = (uiLevel: string): string => {
  const token = normalizeActivityToken(uiLevel);
  if (!token) return '';

  if (token.includes('athlete')) {
    if (token.includes('boy') || token.includes('male')) return 'athlete_boys';
    if (token.includes('girl') || token.includes('female')) return 'athlete_girls';
  }

  if (token.includes('sedentary')) return 'sedentary';
  if (token.includes('lightly_active') || token.includes('low_active')) return 'lightly_active';
  if (token.includes('moderately_active')) return 'moderately_active';
  if (token === 'moderate') return 'moderate';
  if (token === 'active' || token.startsWith('active_')) return 'active';
  if (token.includes('very_active')) return 'very_active';
  if (token.includes('extremely_active')) return 'extremely_active';
  if (token.includes('extra_active')) return 'extremely_active';

  // Preserve unknown values instead of silently coercing to sedentary.
  return String(uiLevel).trim();
};

const formatDateOfBirth = (dateString: string): string => {
  if (!dateString) return '';
  const formattedDate = formatDateStringToYyyyMmDd(dateString);
  return formattedDate || dateString;
};

const toFiniteNumber = (value: string | undefined): number | undefined => {
  if (!value || !value.trim()) return undefined;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeQuestionnaireIds = (questionnaireIds?: string[]): string[] => {
  const ids = (questionnaireIds ?? [])
    .map((id) => id.trim())
    .filter(Boolean);
  return Array.from(new Set(ids));
};

const calculateAgeInMonths = (dateOfBirth: string): number | null => {
  return calculateAgeInMonthsFromDateString(dateOfBirth);
};

/** API requires digits-only phone for quick intake when phone is sent. */
export function phoneDigitsOnly(phone: string): string {
  return String(phone ?? '').replace(/\D/g, '');
}

/** Optional profile fields for POST /clients — only properties the user actually filled. */
function buildFilledClientProfileFields(
  formData: FormData,
  options?: {
    questionnaireIds?: string[];
    applyGrowthFactor?: boolean;
    overrideMinimumEnergyIntake?: boolean;
  }
): Partial<CreateClientPayload> {
  const phoneDigits = phoneDigitsOnly(formData.phone);
  const questionnaireIds = normalizeQuestionnaireIds(options?.questionnaireIds);
  const clientMeasurementSystem = formData.preferredMeasurementSystem || 'metric';
  const weightInKg = toFiniteNumber(formData.weightKg);
  const prePregnancyWeightInKg = toFiniteNumber(formData.prePregnancyWeightKg);
  const heightInCm = toFiniteNumber(formData.heightCm);
  const waistCircumferenceCm = toFiniteNumber(formData.waistCircumferenceCm);
  const hipCircumferenceCm = toFiniteNumber(formData.hipCircumferenceCm);
  const bodyFatPercentage = toFiniteNumber(formData.bodyFatPercentage);
  const parsedCustomCalorieAdjustment = toFiniteNumber(formData.customCalorieAdjustment);
  const customCalorieAdjustment =
    parsedCustomCalorieAdjustment !== undefined &&
    !PRIMARY_GOAL_ADJUSTMENTS.has(parsedCustomCalorieAdjustment)
      ? parsedCustomCalorieAdjustment
      : undefined;

  const ageInMonths = calculateAgeInMonths(formData.dateOfBirth);
  const isInfant = ageInMonths !== null && ageInMonths >= 0 && ageInMonths < 12;
  const isPregnant =
    formData.pregnancyStatus === 'pregnant' ||
    formData.pregnancyStatus === 'first' ||
    formData.pregnancyStatus === 'second' ||
    formData.pregnancyStatus === 'third';
  const lactationPeriod = formData.lactationStatus || 'none';
  const isLactating = !!lactationPeriod && lactationPeriod !== 'none';
  const shouldIncludePregnancyType = isPregnant || isLactating;
  const mappedActivityLevel = formData.activityLevel
    ? mapActivityLevel(formData.activityLevel)
    : undefined;
  const allergies = parseCsvValues(formData.allergies).map((item) => mapAllergyNameToValue(item));
  const intolerances = toFoodIntoleranceApiLabels(parseCsvValues(formData.foodIntolerances));
  const dietaryPreferences = parseCsvValues(formData.dietaryPreferences).map((item) =>
    mapDietaryPreferenceNameToValue(item)
  );
  const cuisineTypesPreferred = parseCsvValues(formData.cuisineTypes).map((item) =>
    mapCuisineTypeNameToValue(item)
  );
  const medicalConditions = mapMedicalConditionsToApiValues(parseCsvValues(formData.medicalConditions));
  const selectedMedicalConditionIds = new Set(medicalConditions.map((condition) => condition.toLowerCase()));
  const medicalConditionSubtypes: Record<string, string> = {};

  if (selectedMedicalConditionIds.has('diabetes')) {
    medicalConditionSubtypes.diabetes = formData.diabetesType?.trim() ?? '';
  }
  if (selectedMedicalConditionIds.has('thyroid')) {
    medicalConditionSubtypes.thyroid = formData.thyroidType?.trim() ?? '';
  }

  const locationTrimmed = formData.location?.trim() ?? '';
  const formattedDateOfBirth = formatDateOfBirth(formData.dateOfBirth);

  const fields: Partial<CreateClientPayload> = {};

  if (formData.firstName?.trim()) fields.firstName = formData.firstName.trim();
  if (formData.lastName?.trim()) fields.lastName = formData.lastName.trim();
  if (phoneDigits) fields.phone = phoneDigits;
  if (formData.phone_country_code?.trim()) {
    fields.phoneCountryCode = formData.phone_country_code.trim();
  }
  if (locationTrimmed) {
    fields.country = locationTrimmed;
    fields.location = locationTrimmed;
  }
  if (formData.cityOrCounty?.trim()) fields.cityOrCounty = formData.cityOrCounty.trim();
  if (formData.zipCode?.trim()) fields.zipCode = formData.zipCode.trim();
  if (formattedDateOfBirth) fields.dateOfBirth = formattedDateOfBirth;
  if (formData.gender?.trim()) fields.gender = formData.gender.trim();
  if (formData.sexAtBirth?.trim()) fields.sexAtBirth = formData.sexAtBirth.trim();
  if (heightInCm !== undefined && heightInCm > 0) fields.heightCm = heightInCm;
  if (weightInKg !== undefined && weightInKg > 0) fields.weightKg = weightInKg;
  if (isPregnant && prePregnancyWeightInKg !== undefined && prePregnancyWeightInKg > 0) {
    fields.prePregnancyWeightKg = prePregnancyWeightInKg;
  }
  if (clientMeasurementSystem) fields.preferredMeasurementSystem = clientMeasurementSystem;
  if (isPregnant) fields.pregnancyTrimester = formData.pregnancyTrimester || 'none';
  if (lactationPeriod && lactationPeriod !== 'none') fields.lactationPeriod = lactationPeriod;
  if (shouldIncludePregnancyType && formData.twinPregnancy === 'twin') {
    fields.isTwinPregnancy = true;
  }
  if (isLactating && formData.weightLossGoal) fields.lactationGoal = formData.weightLossGoal;
  if (!isPregnant && !isLactating && formData.weightLossGoal) {
    fields.weightGoal = formData.weightLossGoal;
  }
  if (formData.formulaId) fields.bmrFormulaId = String(formData.formulaId);
  if (formData.drvFormulaId) fields.drvFormulaId = String(formData.drvFormulaId);
  if (waistCircumferenceCm !== undefined) fields.waistCircumferenceCm = waistCircumferenceCm;
  if (hipCircumferenceCm !== undefined) fields.hipCircumferenceCm = hipCircumferenceCm;
  if (bodyFatPercentage !== undefined) fields.bodyFatPercentage = bodyFatPercentage;
  if (customCalorieAdjustment !== undefined) {
    fields.customCalorieAdjustment = customCalorieAdjustment;
  }
  if (formData.feedingType) {
    fields.feedingType = formData.feedingType as 'breastfed' | 'formula';
  }
  if (!isInfant && mappedActivityLevel) fields.activityLevel = mappedActivityLevel;
  if (allergies.length > 0) fields.allergies = allergies;
  if (intolerances.length > 0) fields.intolerances = intolerances;
  if (dietaryPreferences.length > 0) fields.dietaryPreferences = dietaryPreferences;
  if (cuisineTypesPreferred.length > 0) fields.cuisineTypesPreferred = cuisineTypesPreferred;
  if (medicalConditions.length > 0) fields.medicalConditions = medicalConditions;
  if (Object.keys(medicalConditionSubtypes).length > 0) {
    fields.medicalConditionSubtypes = medicalConditionSubtypes;
  }
  if (options?.overrideMinimumEnergyIntake) fields.overrideMinimumEnergyIntake = true;
  if (typeof options?.applyGrowthFactor === 'boolean') {
    fields.applyGrowthFactor = options.applyGrowthFactor;
  }
  if (questionnaireIds.length > 0) fields.questionnaireIds = questionnaireIds;
  if (formData.pregnancyStatus?.trim()) {
    fields.pregnancyStatus = formData.pregnancyStatus.trim();
  }
  if (formData.healthGoal?.trim()) {
    fields.healthGoal = formData.healthGoal.trim();
  }

  return fields;
}

/** True when the user completed health / measurements (step 2) with meaningful values. */
export function hasFilledHealthProfileFields(formData: FormData): boolean {
  const weight = toFiniteNumber(formData.weightKg);
  const height = toFiniteNumber(formData.heightCm);
  if (weight !== undefined && weight > 0) return true;
  if (height !== undefined && height > 0) return true;
  if (formData.formulaId?.trim()) return true;
  if (formData.drvFormulaId?.trim()) return true;
  if (formData.activityLevel?.trim()) return true;
  if (formData.feedingType?.trim()) return true;
  if (toFiniteNumber(formData.prePregnancyWeightKg) !== undefined) return true;
  if (toFiniteNumber(formData.waistCircumferenceCm) !== undefined) return true;
  if (toFiniteNumber(formData.hipCircumferenceCm) !== undefined) return true;
  if (toFiniteNumber(formData.bodyFatPercentage) !== undefined) return true;
  if (parseCsvValues(formData.allergies).length > 0) return true;
  if (parseCsvValues(formData.foodIntolerances).length > 0) return true;
  if (parseCsvValues(formData.dietaryPreferences).length > 0) return true;
  if (parseCsvValues(formData.cuisineTypes).length > 0) return true;
  if (parseCsvValues(formData.medicalConditions).length > 0) return true;
  const pregnancyStatus = formData.pregnancyStatus?.trim();
  if (
    pregnancyStatus &&
    pregnancyStatus !== 'none' &&
    pregnancyStatus !== 'not_pregnant'
  ) {
    return true;
  }
  if (formData.lactationStatus?.trim() && formData.lactationStatus !== 'none') return true;
  if (formData.weightLossGoal?.trim()) return true;
  if (toFiniteNumber(formData.customCalorieAdjustment) !== undefined) return true;
  if (formData.healthGoal?.trim()) return true;
  return false;
}

type ClientPayloadBuildOptions = {
  useQuickIntake?: boolean;
  sendInviteToApp?: boolean;
  sendEmail?: boolean;
  sendPaymentRequest?: boolean;
  inviteNotes?: string;
  questionnaireIds?: string[];
  applyGrowthFactor?: boolean;
  overrideMinimumEnergyIntake?: boolean;
  /** When completing onboarding from a resumed draft, converts that draft in place instead of creating a new client. */
  draftClientId?: string;
};

/** Unified POST /clients payload — includes every filled profile field; quick intake only when appropriate. */
function buildClientCreatePayload(
  formData: FormData,
  options?: ClientPayloadBuildOptions
): CreateClientPayload {
  const filled = buildFilledClientProfileFields(formData, {
    questionnaireIds: options?.questionnaireIds,
    applyGrowthFactor: options?.applyGrowthFactor,
    overrideMinimumEnergyIntake: options?.overrideMinimumEnergyIntake,
  });

  const payload: CreateClientPayload = {
    email: formData.email.trim(),
    sendInviteToApp: options?.sendInviteToApp ?? false,
    sendEmail: options?.sendEmail ?? false,
    sendPaymentRequest: options?.sendPaymentRequest ?? false,
    ...filled,
  };

  if (options?.useQuickIntake && !hasFilledHealthProfileFields(formData)) {
    payload.intakeMode = 'quick';
  }

  if (options?.inviteNotes?.trim()) {
    payload.inviteNotes = options.inviteNotes.trim().slice(0, 2000);
  }

  if (options?.draftClientId) {
    payload.draftClientId = options.draftClientId;
  }

  return payload;
}

/**
 * POST /clients with intakeMode: "quick" when only step-1 data is present.
 * Step-2 health fields are still included when filled (omits intakeMode so the API accepts them).
 */
export const buildQuickIntakePayload = (
  formData: FormData,
  options?: {
    sendInviteToApp?: boolean;
    sendEmail?: boolean;
    sendPaymentRequest?: boolean;
    inviteNotes?: string;
    questionnaireIds?: string[];
    applyGrowthFactor?: boolean;
    overrideMinimumEnergyIntake?: boolean;
    draftClientId?: string;
  }
): CreateClientPayload =>
  buildClientCreatePayload(formData, {
    ...options,
    useQuickIntake: true,
  });

/** Flexible full profile — omit intakeMode; send every filled field. */
export const buildCreateClientPayload = (
  formData: FormData,
  options?: {
    overrideMinimumEnergyIntake?: boolean;
    sendInviteToApp?: boolean;
    sendPaymentRequest?: boolean;
    inviteNotes?: string;
    applyGrowthFactor?: boolean;
    questionnaireIds?: string[];
    draftClientId?: string;
  }
): CreateClientPayload =>
  buildClientCreatePayload(formData, {
    sendInviteToApp: options?.sendInviteToApp ?? true,
    sendPaymentRequest: options?.sendPaymentRequest ?? false,
    ...options,
    useQuickIntake: false,
  });

/** Quick intake via POST /clients. */
export async function createQuickIntakeClient(
  formData: FormData,
  options?: {
    sendInviteToApp?: boolean;
    sendEmail?: boolean;
    sendPaymentRequest?: boolean;
    inviteNotes?: string;
    questionnaireIds?: string[];
    applyGrowthFactor?: boolean;
  }
) {
  return createClient(buildQuickIntakePayload(formData, options));
}

export const saveClient = async (
  formData: FormData,
  setIsSaving: (loading: boolean) => void,
  onAddClient: (clientData: Partial<Client>, refreshClients: () => void) => void,
  resetForm: () => void,
  options?: {
    sendInviteToApp?: boolean;
    sendPaymentRequest?: boolean;
    inviteNotes?: string;
    applyGrowthFactor?: boolean;
    sendEmail?: boolean;
    questionnaireIds?: string[];
    /** Quick intake (POST /clients with intakeMode: "quick"). Omit for flexible full profile. */
    intakeMode?: 'quick';
    /** When completing onboarding from a resumed draft, converts that draft in place instead of creating a new client. */
    draftClientId?: string;
  }
): Promise<SaveClientResult> => {
  setIsSaving(true);
  try {
    const submit = async (overrideMinimumEnergyIntake?: boolean) => {
      const sharedOpts = {
        overrideMinimumEnergyIntake: !!overrideMinimumEnergyIntake,
        sendInviteToApp: options?.sendInviteToApp ?? true,
        sendPaymentRequest: options?.sendPaymentRequest ?? false,
        inviteNotes: options?.inviteNotes,
        applyGrowthFactor: options?.applyGrowthFactor,
        questionnaireIds: options?.questionnaireIds,
        draftClientId: options?.draftClientId,
      };

      const useQuickIntake = options?.intakeMode === 'quick';

      return await createClient(
        buildClientCreatePayload(formData, {
          ...sharedOpts,
          sendInviteToApp: options?.sendInviteToApp ?? (useQuickIntake ? false : true),
          sendPaymentRequest: options?.sendPaymentRequest ?? false,
          sendEmail: options?.sendEmail ?? false,
          useQuickIntake,
        })
      );
    };

    try {
      // First attempt: try without override parameter
      const response = await submit(false);
      const clientId = extractClientIdFromApiResponse(response);

      // Trigger refresh after successful client creation
      // The refresh function will be called by the parent component
      onAddClient({} as Partial<Client>, () => {
        // This will be replaced by the actual refresh function from parent
      });
      resetForm();

      showSuccessToast(
        "Success",
        "Client added successfully!"
      );
      return { success: true, clientId };
    } catch (error: any) {
      // Check if error is related to minimum energy intake
      const isMinimumEnergyError = isMinimumEnergyIntakeError(error);
      
      if (isMinimumEnergyError) {
        // Show warning and retry with override parameter
        const confirmed = window.confirm(
          'Warning: The calculated calories are below the minimum energy intake requirement. Do you want to override this restriction and proceed?'
        );
        
        if (!confirmed) {
          // User cancelled - don't show error, just return false
          return { success: false };
        }
        
        // Retry with override parameter
        try {
          const retryResponse = await submit(true);
          const clientId = extractClientIdFromApiResponse(retryResponse);

          // Trigger refresh after successful client creation
          onAddClient({} as Partial<Client>, () => {
            // This will be replaced by the actual refresh function from parent
          });
          resetForm();

          showSuccessToast(
            "Success",
            "Client added successfully with minimum energy intake override!"
          );
          return { success: true, clientId };
        } catch (retryError: any) {
          console.error('Client creation retry error:', retryError);
          showErrorToast(retryError, "Error");
          return { success: false };
        }
      } else {
        // For other errors, show error toast
        showErrorToast(error, "Error");
        return { success: false };
      }
    }
  } catch (error: any) {
    console.error('Unexpected error in saveClient:', error);
    showErrorToast(error, "Error");
    return { success: false };
  } finally {
    setIsSaving(false);
  }
};
